import {
    Dialog,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { CloseLine } from '@archipro-design/icons';
import type { TeamMemberView } from '@modules/professional/type/team-member';
import { useClickAway } from 'ahooks';
import { useRef } from 'react';

import * as S from './TeamMemberDetailsModalUpdated.style';
import { hasRenderableTeamMemberSummary } from './hasRenderableTeamMemberSummary';
import TeamMemberModalContent from './TeamMemberModalContent';
import BottomDrawer from '~/modules/detail/components/bottom-drawer/BottomDrawer';

interface TeamMemberDetailsModalUpdatedProps {
    member: TeamMemberView;
    website: string;
    toggleModal: () => void;
    isOpen: boolean;
    /** The professional this team member belongs to, credited on a phone reveal. */
    professionalId?: number;
}

const TeamMemberDetailsModalUpdated = ({
    member,
    website,
    toggleModal,
    isOpen,
    professionalId,
}: TeamMemberDetailsModalUpdatedProps) => {
    const { css } = useStyles();
    const modalRef = useRef(null);
    const { mobile: isMobile } = useAppDisplayModeContext();

    useClickAway(() => {
        toggleModal();
    }, [modalRef]);

    if (member === null) return null;

    const hasSummary = hasRenderableTeamMemberSummary(member.summary);
    /** Wide shell only when profile + description column layout is needed. */
    const modalWidth = hasSummary ? 1240 : 620;

    return (
        <>
            {isMobile ? (
                <BottomDrawer open={isOpen} toggleModal={toggleModal}>
                    <TeamMemberModalContent
                        website={website}
                        memberDetails={member}
                        professionalId={professionalId}
                    />
                </BottomDrawer>
            ) : (
                <Dialog
                    ref={modalRef}
                    open={isOpen}
                    trapFocus={false}
                    onCancel={toggleModal}
                    header={null}
                    headerAction={{
                        icon: (
                            <CloseLine
                                className={css({
                                    width: `${pxToRem(24)} !important`,
                                    height: `${pxToRem(24)} !important`,
                                })}
                            />
                        ),
                        title: 'Close',
                        onClick: toggleModal,
                    }}
                    content={{
                        content: (
                            <TeamMemberModalContent
                                website={website}
                                memberDetails={member}
                                professionalId={professionalId}
                            />
                        ),
                        className: css(S.TeamMemberDialogContentWrapper),
                        style: { padding: '0' },
                    }}
                    footer={{
                        style: { padding: '0' },
                    }}
                    variables={{
                        border: 0,
                        rootBorderRadius: 0,
                        rootWidth: pxToRem(modalWidth),
                        rootMaxHeight: 'min(90vh, 100vh)',
                        overflow: true,
                        padding: pxToRem(0),
                    }}
                />
            )}
        </>
    );
};

export default TeamMemberDetailsModalUpdated;
